/**
 * フォームが送信可能な状態か（必須項目が全て入力され、かつ検証エラーが無いか）を返すフック。
 *
 * 送信ボタンの `disabled` 制御に使用する。判定には
 * `validateFields({ validateOnly: true })` を用いる。`validateOnly` は検証結果を
 * 画面のエラー表示へ反映しないため、フォームを開いた直後に赤字エラーが並ぶことはない。
 *
 * `isFieldsTouched` を使わない理由:
 * 編集時のプリフィル（`setFieldsValue`）や `initialValues` で設定された値は
 * 「操作された」扱いにならず、正しい入力であってもボタンが無効のままになるため。
 * 本フックは「操作されたか」ではなく「現在の値が妥当か」で判定する。
 */
import { useEffect, useState } from 'react';
import { Form } from 'antd';
import type { FormInstance } from 'antd';

export function useIsFormSubmittable<FormValues>(params: {
  readonly form: FormInstance<FormValues>;
  /** フォームが表示されているか。非表示のフォームは検証対象外とする */
  readonly isFormVisible: boolean;
}): boolean {
  const { form, isFormVisible } = params;
  const watchedValues = Form.useWatch([], form);
  const [isSubmittable, setIsSubmittable] = useState<boolean>(false);

  useEffect(() => {
    // 入力欄がまだ登録されていない状態（モーダルを開いた直後の一瞬など）では
    // `validateFields` が「検証対象なし＝成功」で解決し、送信可能と誤判定される。
    // 登録済みの入力欄が現れるまでは送信不可として扱う。
    const hasRegisteredFields = form.getFieldsError().length > 0;
    if (!isFormVisible || !hasRegisteredFields) {
      setIsSubmittable(false);
      return;
    }

    // アンマウント後・再検証開始後に古い結果で状態を更新しないためのフラグ
    let isEffectActive = true;

    form
      .validateFields({ validateOnly: true })
      .then(() => {
        if (isEffectActive) {
          setIsSubmittable(true);
        }
      })
      .catch(() => {
        if (isEffectActive) {
          setIsSubmittable(false);
        }
      });

    return () => {
      isEffectActive = false;
    };
  }, [form, isFormVisible, watchedValues]);

  return isSubmittable;
}
